import { inject, Service } from '@angular/core';
import type { TranslateService } from '@ngx-translate/core';
import type {
  OnyxColor,
  OnyxConfirmModalData,
  OnyxCoordinates,
  OnyxHttpErrorResponse,
  OnyxIconName,
  OnyxMapRouteLegProgress,
  OnyxOptional,
  OnyxRoute,
  OnyxRoutePoint,
  OnyxRouteSummary,
} from '@onyx/angular';
import {
  AddressHelper,
  MapHelper,
  OnyxConfirmModalComponent,
  OnyxMapRoutePointType,
  OnyxModalService,
  OnyxScheduleCompliance,
  OnyxToastService,
  UtilityHelper,
} from '@onyx/angular';
import {
  chain,
  chunk,
  clamp,
  initial,
  isBoolean,
  isEqual,
  isNumber,
  isString,
  minBy,
  tail,
  zip,
} from 'lodash';
import { DateTime, Duration } from 'luxon';
import type { Observable, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs';
import { TechnicalPointCategory } from '../../dashboard/fleet/common/enums/technical-point-category';
import { DelegationPointType } from '../../dashboard/fleet/common/enums/technical-point-type';
import { FleetTypeHelper } from '../../dashboard/fleet/common/helpers/fleet-type.helper';
import type {
  DelegationPoint,
  SwapData,
  SwapPoint,
  TechnicalPoint,
} from '../../dashboard/fleet/common/interfaces/technical-point';
import { FleetService } from '../../dashboard/fleet/common/services/fleet.service';
import { MapVehiclePositionSource } from '../../dashboard/map/common/enums/map-vehicle-position-source';
import { OrderFormRouteType } from '../../dashboard/orders/common/enums/order-form-route-type';
import { OrderPointCategory } from '../../dashboard/orders/common/enums/order-point-category';
import { OrderPointStatus } from '../../dashboard/orders/common/enums/order-point-status';
import { OrderStatus } from '../../dashboard/orders/common/enums/order-status';
import { OrderTachographActionReason } from '../../dashboard/orders/common/enums/order-tachograph-action-reason';
import { OrderTimeWindowType } from '../../dashboard/orders/common/enums/order-time-window-type';
import { OrderTransitPointTimeWindowType } from '../../dashboard/orders/common/enums/order-transit-point-time-window-type';
import { OrderHelper } from '../../dashboard/orders/common/helpers/order.helper';
import type {
  Order,
  SimplifiedOrder,
} from '../../dashboard/orders/common/interfaces/order';
import type {
  OrderPoint,
  OrderPointEstimations,
  OrderPointTimestamps,
} from '../../dashboard/orders/common/interfaces/order-point';
import type { OrderPointAction } from '../../dashboard/orders/common/interfaces/order-point-action';
import type { OrderPointDriverReports } from '../../dashboard/orders/common/interfaces/order-point-driver-reports';
import type { OrderFormService } from '../../dashboard/orders/common/services/order-form.service';
import type { OrderPointForm } from '../../dashboard/orders/order-form/order-points-form/order-point-form/order-point-form.component';
import { DAY_TIME_RANGE } from '../constants/common/day-time-range';
import { SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD } from '../constants/common/schedule-compliance-delay-risk-threshold';
import { VALIDATION_ERROR_CODES } from '../constants/common/validation-error-codes';
import { TELEMATICS_OFFLINE_THRESHOLD } from '../constants/telematics/telematics-offline-threshold';
import { DictionaryCode } from '../enums/dictionary-code';
import type { BusinessHours } from '../interfaces/common/business-hours';
import type { Point } from '../interfaces/common/point';
import type { RelatedFleetSwap } from '../interfaces/common/related-fleet-swap';
import type { OffRouteData } from '../interfaces/route/off-route-data';
import type { RoutePointRealization } from '../interfaces/route/route-point-realization';
import type { RouteProposal } from '../interfaces/route/route-proposal';
import type { TelematicsItem } from '../interfaces/telematics/telematics-item';
import type {
  TelematicsRoutePoint,
  TelematicsRoutePointDto,
  TelematicsTechnicalRoutePoint,
} from '../interfaces/telematics/telematics-route-point';
import type { TelematicsRoutePointOrder } from '../interfaces/telematics/telematics-route-point-order';
import { TelematicsRoutePointStatus } from '../interfaces/telematics/telematics-vehicle-point-status';
import type {
  RoutePoint,
  RoutePointKey,
  RouteService,
} from '../services/route.service';
import type { TelematicsStore } from '../stores/telematics.store';
import { DateTimeHelper } from './date-time.helper';
import type { DeadlineType } from './deadline.helper';
import { DeadlineHelper } from './deadline.helper';
import { RoutePointValueHelper } from './route-point-value.helper';
import { ScheduleComplianceHelper } from './schedule-compliance.helper';

export type RoutePointValue = OrderPoint | OrderPointForm | TechnicalPoint;

export interface RoutePointData {
  deadlineTime: DateTime | null;
  bufferTime: Duration | null;
  compliance: OnyxScheduleCompliance | null;
  complianceColor: OnyxColor | null;
  realization: RoutePointRealization | null;
  estimations: OrderPointEstimations | null;
  status: OrderPointStatus | null;
}

@Service()
export class RouteHelper {
  private readonly modalService = inject(OnyxModalService);
  private readonly toastService = inject(OnyxToastService);
  private readonly fleetService = inject(FleetService);

  private readonly I18N = 'route';

  public cancelSwap(uuid: string, close$?: Subject<void>): void {
    this.modalService
      .open<OnyxConfirmModalData, boolean>(OnyxConfirmModalComponent, {
        heading: `${this.I18N}.cancelSwapHeading`,
        message: `${this.I18N}.cancelSwapMessage`,
        confirmButtonText: 'labels.revoke',
      })
      .result.pipe(
        filter((ok) => !!ok),
        switchMap(() => this.fleetService.deleteSwap(uuid)),
      )
      .subscribe(() => {
        this.toastService.showSuccess({
          message: `${this.I18N}.swapCancelled`,
        });
        close$?.next();
      });
  }

  public static hasCompletedSwap(points: OnyxOptional<SwapPoint[]>): boolean {
    return (
      points?.some(
        (point) => RouteHelper.getStatus(point) === OrderPointStatus.COMPLETED,
      ) ?? false
    );
  }

  public static getPointAction(
    point: OnyxOptional<RoutePoint>,
    status: OnyxOptional<OrderPointStatus>,
    compliance: OnyxOptional<OnyxScheduleCompliance>,
    isCurrent: boolean | RouteService,
    translateService: TranslateService,
    customIcons = false,
  ): OrderPointAction | null {
    if (!point || !status) return null;

    if (RouteHelper.isProposition(point)) {
      return {
        icon: 'proposition-circle-filled',
        tooltip: 'labels.orderProposal',
      };
    }

    const routeService = !isBoolean(isCurrent) ? isCurrent : null;
    isCurrent = isBoolean(isCurrent) ? isCurrent : isCurrent.isCurrent(point);
    const value = RoutePointValueHelper.getValue(point);
    const isHandlingPoint = RouteHelper.isHandlingPoint(value);
    const orderStatus =
      routeService?.order()?.status?.value ??
      ('order' in point && point.order ? point.order.status.value : null);

    let icon = RouteHelper.getStatusIcon(value, status);
    let animate: OrderPointAction['animate'];

    if (customIcons) {
      if (status === OrderPointStatus.WAITING) {
        icon = 'vehicle-waiting-alt-2-filled';
        animate = 'spin';
      } else if (status === OrderPointStatus.IN_PROGRESS && isHandlingPoint) {
        icon = 'menu-orders-filled';
        animate = 'opacity';
      } else if (
        status === OrderPointStatus.TO_DO &&
        orderStatus === OrderStatus.IN_PROGRESS &&
        isCurrent
      ) {
        icon = 'vehicle-ride-filled';
      }
    }

    let statusLabel: string;
    if (status === OrderPointStatus.TO_DO && isCurrent) {
      statusLabel = translateService.instant('labels.driving');
    } else if (status === OrderPointStatus.IN_PROGRESS && isHandlingPoint) {
      statusLabel = translateService.instant(
        `labels.${value.category}InProgress`,
      );
    } else {
      statusLabel = translateService.instant(
        `${DictionaryCode.ORDER_POINT_STATUS}.${status}`,
      );
    }

    const complianceLabel = compliance
      ? translateService.instant(`labels.${compliance}`)
      : null;

    return {
      icon,
      tooltip:
        status === OrderPointStatus.CANCELED
          ? statusLabel
          : `${statusLabel}${complianceLabel ? ` · ${complianceLabel}` : ''}`,
      ...(animate ? { animate } : {}),
    };
  }

  public static getStatusIcon(
    point: RoutePointValue,
    status: OrderPointStatus,
  ): OnyxIconName {
    return (
      {
        [OrderPointStatus.TO_DO]: 'clock-filled',
        [OrderPointStatus.WAITING]: 'vehicle-waiting-filled',
        [OrderPointStatus.IN_PROGRESS]: RouteHelper.getInProgressIcon(point),
        [OrderPointStatus.COMPLETED]: 'check-circle-filled',
        [OrderPointStatus.CANCELED]: 'no-entry-filled',
      } satisfies Record<OrderPointStatus, OnyxIconName>
    )[status];
  }

  public static getInProgressIcon(point: RoutePointValue): OnyxIconName {
    const type = RouteHelper.getType(point);
    const showVehicleIcon = [
      OnyxMapRoutePointType.LOADING,
      OnyxMapRoutePointType.UNLOADING,
    ].includes(type as OnyxMapRoutePointType);
    const circleIconName = `${showVehicleIcon ? `vehicle-${type}` : type}-circle-filled`;

    return UtilityHelper.isOnyxIcon(circleIconName)
      ? circleIconName
      : MapHelper.getPointIconByType(type);
  }

  public static getCurrentPoint<T extends RoutePoint>(
    points: T[],
    order: OnyxOptional<SimplifiedOrder>,
  ): T | null {
    return (
      points.find((point) => {
        order = 'order' in point ? point.order : order;
        if (order) {
          const orderStatus = order.status.value;
          if (
            ![OrderStatus.TO_DO, OrderStatus.IN_PROGRESS].includes(orderStatus)
          ) {
            return false;
          }
        }

        const status = RouteHelper.getStatus(point);
        return (
          status != null &&
          ![OrderPointStatus.COMPLETED, OrderPointStatus.CANCELED].includes(
            status,
          )
        );
      }) ?? null
    );
  }

  public static getLastCompletedPoint(points: RoutePoint[]): RoutePoint | null {
    return points.findLast((point) => RouteHelper.isCompleted(point)) ?? null;
  }

  public static isOrderCompleted(
    point: RoutePoint,
    points: RoutePoint[],
    order?: OnyxOptional<SimplifiedOrder>,
  ): boolean {
    if ('order' in point) {
      if (!point.order) return false;

      const orderUuid = point.order.uuid;
      const orderPoints = points.filter(
        (routePoint) =>
          'order' in routePoint && routePoint.order?.uuid === orderUuid,
      );

      return orderPoints.every(
        (routePoint) =>
          RouteHelper.getStatus(routePoint) === OrderPointStatus.COMPLETED,
      );
    }

    return OrderHelper.isFinished(order?.status.value ?? null);
  }

  public static getPointRealizationTime(
    point: OnyxOptional<Point>,
    options?: { includeServiceTime?: boolean; compareToNow?: boolean },
  ): DateTime | null {
    if (!point) return null;

    const { includeServiceTime = true, compareToNow = true } = options ?? {};

    const getDateTime = (
      date: OnyxOptional<string>,
      includeServiceTime = false,
    ): DateTime => {
      if (!date) return DateTime.now();

      const result = DateTime.fromISO(date);
      if (!includeServiceTime) return result;

      return result.plus({ seconds: point.serviceTime ?? 0 });
    };

    switch (point.status) {
      case OrderPointStatus.TO_DO:
        return compareToNow
          ? DateTimeHelper.max(point.estimations?.arrivalTime ?? 'now', 'now')
          : point.estimations?.arrivalTime
            ? DateTime.fromISO(point.estimations.arrivalTime)
            : null;

      case OrderPointStatus.WAITING:
        return getDateTime(point.timestamps.arrivedAt);

      case OrderPointStatus.IN_PROGRESS:
        return getDateTime(point.timestamps.startedAt, includeServiceTime);

      case OrderPointStatus.COMPLETED:
        return getDateTime(point.timestamps.completedAt);

      case OrderPointStatus.CANCELED:
        return null;
    }
  }

  public static getScheduleCompliance(
    point: RoutePoint,
    points: RoutePoint[],
    routeService?: OnyxOptional<RouteService>,
    orderFormService?: OnyxOptional<OrderFormService>,
  ): OnyxScheduleCompliance | null {
    const value = RoutePointValueHelper.getValue(point);
    if (RoutePointValueHelper.isOrderFormPoint(value)) {
      const index = routeService?.getIndex(point) ?? -1;
      return (
        orderFormService?.activeRouteCompliance()?.pointsCompliance.at(index) ??
        null
      );
    }

    if (RouteHelper.isReturningDelegationPoint(point)) {
      const delegationData = RouteHelper.getDelegationData(point);
      if (!delegationData) return null;

      const { date, time } = delegationData.delegation.delegation.end;
      const endDate = DateTime.fromISO(`${date}T${time}`);

      return RouteHelper.getBufferCompliance(endDate.diffNow(), value.status);
    }

    const realizationTime = RouteHelper.getPointRealizationTime(value);
    if (!realizationTime) return null;

    const deadline =
      DeadlineHelper.getPointDeadline(value) ??
      ('deadlineTime' in point &&
        !!point.deadlineTime && {
          arrival: point.deadlineTime,
          departure: point.deadlineTime,
        });
    if (!deadline) return null;

    const deadlineTime =
      value.status === OrderPointStatus.TO_DO
        ? deadline.arrival
        : deadline.departure;
    if (!deadlineTime) return null;

    return RouteHelper.getBufferCompliance(
      deadlineTime.diff(realizationTime),
      value.status,
    );
  }

  public static getRealization(
    point: RoutePoint,
    now: DateTime,
  ): RoutePointRealization | null {
    const status = RouteHelper.getStatus(point);
    if (
      status !== OrderPointStatus.WAITING &&
      status !== OrderPointStatus.IN_PROGRESS
    ) {
      return null;
    }

    const timestamps = RouteHelper.getTimestamps(point);
    if (!timestamps) return null;

    return this.buildRealization({
      status,
      from: timestamps.startedAt ?? timestamps.arrivedAt!,
      to: now,
    });
  }

  public static getRealizationHistory(
    point: RoutePoint,
  ): RoutePointRealization[] {
    const timestamps = RouteHelper.getTimestamps(point);
    if (!timestamps) return [];

    const { arrivedAt, startedAt, completedAt } = timestamps;

    const history: RoutePointRealization[] = [];
    if (startedAt && arrivedAt) {
      history.push(
        RouteHelper.buildRealization({
          status: OrderPointStatus.WAITING,
          from: arrivedAt,
          to: startedAt,
        }),
      );
    }
    if (completedAt && startedAt) {
      history.push(
        RouteHelper.buildRealization({
          status: OrderPointStatus.IN_PROGRESS,
          from: startedAt,
          to: completedAt,
        }),
      );
    }

    return history;
  }

  public static getIndex(points: RoutePoint[], point: RoutePoint): number {
    return points.findIndex((p) => RouteHelper.isEqual(points, p, point));
  }

  public static getKey(
    points: RoutePoint[],
    point: RoutePoint | number,
  ): RoutePointKey | null {
    const routePoint = isNumber(point) ? points.at(point) : point;
    if (!routePoint) return null;

    if ('uuid' in routePoint) return routePoint.uuid;
    if ('form' in routePoint) return routePoint.form;

    return RouteHelper.getUuid(routePoint);
  }

  public static isEqual(
    points: RoutePoint[],
    a: OnyxOptional<RoutePoint | number>,
    b: OnyxOptional<RoutePoint | number>,
  ): boolean {
    if (a == null || b == null) return false;

    const aKey = RouteHelper.getKey(points, a);
    const bKey = RouteHelper.getKey(points, b);
    if (!aKey || !bKey) return false;

    return aKey === bKey;
  }

  public static isOrderDedicated(
    order: TelematicsRoutePointOrder | Order,
  ): boolean {
    if ('isDedicated' in order) return order.isDedicated;
    return !!order.parameters.isDedicated;
  }

  public static isTechnicalPoint(
    point: RoutePoint | RoutePointValue,
  ): point is TechnicalPoint {
    const category = RouteHelper.getCategory(point);
    return Object.values(TechnicalPointCategory).some(
      (technicalCategory) => technicalCategory === category,
    );
  }

  public static getPointValue({
    point,
    technicalPoint,
  }: TelematicsRoutePoint): Point {
    return point ?? technicalPoint;
  }

  public static getUuid(point: RoutePoint | TelematicsRoutePointDto): string {
    if ('value' in point) return point.value.uuid;
    if ('point' in point && point.point) return point.point.uuid;
    if ('technicalPoint' in point) return point.technicalPoint.uuid;

    return point.uuid;
  }

  public static getStatus(
    point: RoutePoint | TelematicsRoutePointDto,
  ): OrderPointStatus | null {
    if ('point' in point && point.point) return point.point.status;
    if ('technicalPoint' in point) return point.technicalPoint.status;

    return 'status' in point ? point.status : null;
  }

  public static getTimestamps(
    point: RoutePoint | TelematicsRoutePointDto,
  ): OrderPointTimestamps | null {
    if ('point' in point && point.point) return point.point.timestamps;
    if ('technicalPoint' in point) return point.technicalPoint.timestamps;

    return 'timestamps' in point ? point.timestamps : null;
  }

  public static getWaitingForServiceDurationWarning(
    point: RoutePoint | RoutePointValue,
  ): Duration | null {
    const value = RoutePointValueHelper.getValue(point);
    const tachographActions =
      'tachographActions' in value ? value.tachographActions : null;
    if (!tachographActions?.length) return null;

    const waitingForServiceDuration = chain(tachographActions)
      .filter(
        (action) =>
          action.reason === OrderTachographActionReason.WAITING_FOR_SERVICE,
      )
      .map(({ timeWindow }) =>
        DateTime.fromISO(timeWindow.to)
          .diff(DateTime.fromISO(timeWindow.from))
          .toMillis(),
      )
      .sum()
      .thru((duration) => Duration.fromMillis(duration))
      .value();
    if (waitingForServiceDuration.as('hours') < 1) return null;

    return waitingForServiceDuration;
  }

  public static isFinished(point: RoutePoint): boolean {
    return RouteHelper.isCompleted(point) || RouteHelper.isCanceled(point);
  }

  public static isCompleted(point: RoutePoint): boolean {
    return RouteHelper.getStatus(point) === OrderPointStatus.COMPLETED;
  }

  public static isCanceled(point: RoutePoint): boolean {
    return RouteHelper.getStatus(point) === OrderPointStatus.CANCELED;
  }

  public static getCompletedAt(point: RoutePoint): string | null {
    const timestamps = RouteHelper.getTimestamps(point);
    return timestamps?.completedAt ?? null;
  }

  public static getDriverReportedStatuses(
    point: RoutePoint | RoutePointValue,
  ): Set<OrderPointStatus> {
    const value = RoutePointValueHelper.getValue(point);
    const driverReports = 'driverReports' in value ? value.driverReports : null;
    if (!driverReports) return new Set();

    return new Set(
      Object.values(OrderPointStatus).filter(
        (status) => driverReports[status as keyof OrderPointDriverReports],
      ),
    );
  }

  public static getStatusColor(
    point: RoutePoint,
    now: DateTime,
    type: DeadlineType,
    options?: { skipWithoutTime: boolean },
  ): OnyxColor | null {
    const isCanceled = RouteHelper.isCanceled(point);
    if (isCanceled) return null;

    const status = RouteHelper.getStatus(point);
    const timestamps = RouteHelper.getTimestamps(point);
    const isToDo = status === OrderPointStatus.TO_DO;
    const isCompleted = RouteHelper.isCompleted(point);
    const isArrival = type === 'arrival';

    const time = isArrival
      ? isToDo
        ? RoutePointValueHelper.getEstimations(point)?.arrivalTime
        : timestamps?.arrivedAt
      : isCompleted
        ? RouteHelper.getCompletedAt(point)
        : RoutePointValueHelper.getEstimations(point)?.departureTime;

    if (options?.skipWithoutTime && !time) return null;

    const compareTime = time
      ? DateTime.fromISO(time)
      : (isArrival ? isToDo : !isCompleted)
        ? now
        : null;
    const deadline = DeadlineHelper.getPointDeadline(point)?.[type];
    if (compareTime && deadline && deadline.diff(compareTime).toMillis() < 0) {
      return 'red';
    }

    const isDone = isArrival ? !isToDo : isCompleted;
    if (!deadline) return isDone && time ? 'green' : null;
    if (!compareTime) return null;

    if (isArrival) {
      if (!isToDo) return 'green';
      if (
        deadline.diff(compareTime) <= SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD
      ) {
        return 'orange';
      }

      return null;
    }

    return isCompleted ? 'green' : null;
  }

  public static getChainScheduleCompliance(
    point: RoutePoint,
    points: RoutePoint[],
    type: DeadlineType,
  ): OnyxScheduleCompliance | null {
    if (RouteHelper.isCanceled(point)) return null;

    const deadline = DeadlineHelper.getChainDeadline(point, points)?.[type];
    if (!deadline) return null;

    const status = RouteHelper.getStatus(point);
    const timestamps = RouteHelper.getTimestamps(point);
    const isCompleted = RouteHelper.isCompleted(point);

    const time =
      type === 'arrival'
        ? status === OrderPointStatus.TO_DO
          ? RoutePointValueHelper.getEstimations(point)?.arrivalTime
          : timestamps?.arrivedAt
        : isCompleted
          ? RouteHelper.getCompletedAt(point)
          : RoutePointValueHelper.getEstimations(point)?.departureTime;
    if (!time) return null;

    return RouteHelper.getBufferCompliance(
      deadline.diff(DateTime.fromISO(time)),
      status,
    );
  }

  public static hasChainTermConflict(
    point: RoutePoint,
    points: RoutePoint[],
    type: DeadlineType,
  ): boolean {
    return (
      RouteHelper.getChainScheduleCompliance(point, points, type) ===
      OnyxScheduleCompliance.DELAYED
    );
  }

  public static getCategory(
    point: RoutePoint | RoutePointValue,
  ): OrderPointCategory | TechnicalPointCategory | null {
    if (!point) return null;

    if ('point' in point && point.point) return point.point.category;
    if ('technicalPoint' in point) return point.technicalPoint.category;

    return 'category' in point ? point.category : null;
  }

  public static getType(
    point: RoutePoint | RoutePointValue,
  ): OnyxMapRoutePointType {
    const value = RoutePointValueHelper.getValue(point);
    const isTechnicalPoint = RouteHelper.isTechnicalPoint(value);

    return (
      isTechnicalPoint ? value.category : value.type
    ) as OnyxMapRoutePointType;
  }

  public static getSwapData(routePoint: RoutePoint): SwapData | null {
    if (!('technicalPoint' in routePoint)) return null;

    const point = RoutePointValueHelper.getValue(routePoint);
    return point.category === TechnicalPointCategory.SWAP_POINT
      ? (point.swapData ?? null)
      : null;
  }

  public static isSwapPoint(
    point: RoutePoint | RoutePointValue | null,
  ): point is SwapPoint {
    return (
      !!point &&
      RouteHelper.getCategory(point) === TechnicalPointCategory.SWAP_POINT
    );
  }

  public static getSwapCurrentRoutePoint(
    relatedFleetSwap: RelatedFleetSwap | null,
    vehicleUuid: OnyxOptional<string>,
    telematicsStore: TelematicsStore,
    order: OnyxOptional<Order>,
  ): TelematicsTechnicalRoutePoint | null {
    if (!relatedFleetSwap) return null;

    const swapUuid = relatedFleetSwap.uuid;
    const telematicsSwapItems = (
      telematicsStore.telematicsItemsBySwapUuid().get(swapUuid) ?? []
    ).filter(
      (item) =>
        item.currentPoint &&
        RouteHelper.getSwapData(item.currentPoint)?.uuid === swapUuid,
    );

    let item: OnyxOptional<TelematicsItem> = null;
    if (vehicleUuid) {
      item = telematicsSwapItems.find(({ uuid }) => uuid === vehicleUuid);
    } else if (order) {
      const currentPointUuid = OrderHelper.getCurrentPoint(order.points)?.uuid;
      const items = telematicsSwapItems.filter(({ routePoints }) =>
        routePoints.some(
          (routePoint) => routePoint.point?.uuid === currentPointUuid,
        ),
      );
      item = items.length === 1 ? items[0] : null;
    }

    const currentPoint = item?.currentPoint;
    if (
      !currentPoint?.technicalPoint ||
      RouteHelper.getSwapData(currentPoint)?.uuid !== swapUuid
    ) {
      return null;
    }

    return currentPoint;
  }

  public static isDelegationPoint(
    point: RoutePoint | RoutePointValue,
    type?: `${DelegationPointType}` | null,
  ): point is DelegationPoint {
    const value = RoutePointValueHelper.getValue(point);
    return (
      value.category === TechnicalPointCategory.DELEGATION &&
      (!type || value.type === type)
    );
  }

  public static getDelegationData(
    routePoint: RoutePoint,
  ): DelegationPoint['delegationData'] | null {
    if (!('technicalPoint' in routePoint)) return null;

    const point = RoutePointValueHelper.getValue(routePoint);
    return point.category === TechnicalPointCategory.DELEGATION
      ? point.delegationData
      : null;
  }

  public static getDelegationUuids(routePoint: RoutePoint): string[] {
    const primary =
      'primaryDriverDelegationUuid' in routePoint
        ? routePoint.primaryDriverDelegationUuid
        : null;
    const secondary =
      'secondaryDriverDelegationUuid' in routePoint
        ? routePoint.secondaryDriverDelegationUuid
        : null;

    return [primary, secondary].filter((uuid) => uuid != null);
  }

  public static getLastDelegationCyclePoints(
    points: RoutePoint[],
  ): RoutePoint[] {
    const cutOffIndex = points.findLastIndex((point) =>
      RouteHelper.isDelegationPoint(point, DelegationPointType.START),
    );
    if (cutOffIndex === -1) return [];

    return points.slice(cutOffIndex);
  }

  public static hasActiveCycle(cyclePoints: RoutePoint[]): boolean {
    return cyclePoints.some((point) => {
      if (RouteHelper.getDelegationData(point)?.hasCycleActivePoints) {
        return true;
      }
      if (RouteHelper.isDelegationPoint(point)) return false;

      const status = 'cycleIndex' in point ? point.status : null;
      if (!status) return false;

      return ![
        TelematicsRoutePointStatus.COMPLETED,
        TelematicsRoutePointStatus.CANCELED,
      ].includes(status);
    });
  }

  public static isReturningDelegationPoint(point: RoutePoint): boolean {
    const value = RoutePointValueHelper.getValue(point);
    if (!RouteHelper.isDelegationPoint(value, DelegationPointType.END)) {
      return false;
    }

    return value.delegationData.delegation.delegation.isReturning;
  }

  public static getFallbackTrailer(vehicle: TelematicsItem | null): {
    uuid: string;
    category: TelematicsItem['generalInformation']['category'];
    coordinates: OnyxCoordinates;
  } | null {
    if (!vehicle || !FleetTypeHelper.isTrailer(vehicle)) return null;

    const { coordinates, source } = RouteHelper.getVehiclePosition(vehicle);
    if (
      !coordinates ||
      ![
        MapVehiclePositionSource.LAST_KNOWN_POSITION,
        MapVehiclePositionSource.LAST_KNOWN_POINT,
      ].includes(source)
    ) {
      return null;
    }

    return {
      uuid: vehicle.uuid,
      category: vehicle.generalInformation.category,
      coordinates,
    };
  }

  public static getVehiclePosition(vehicle: TelematicsItem | null): {
    source: MapVehiclePositionSource;
    coordinates: OnyxOptional<OnyxCoordinates>;
  } {
    const unknownLocation = {
      source: MapVehiclePositionSource.NONE,
      coordinates: null,
    };

    if (!vehicle) return unknownLocation;

    const { coordinates, updatedAt } = vehicle.telemetry ?? {};
    const isLive =
      updatedAt &&
      DateTime.fromISO(updatedAt) >=
        DateTime.now().minus(TELEMATICS_OFFLINE_THRESHOLD);

    if (isLive && coordinates) {
      return {
        source: MapVehiclePositionSource.LIVE_POSITION,
        coordinates,
      };
    }

    const lastPoint = vehicle.lastPoint
      ? RouteHelper.getPointValue(vehicle.lastPoint)
      : null;
    const lastPointCoordinates = lastPoint
      ? 'address' in lastPoint
        ? lastPoint.address.coordinates
        : lastPoint.endAddress.coordinates
      : null;
    const lastPointCompletedAt = vehicle.lastPoint
      ? RouteHelper.getTimestamps(vehicle.lastPoint)?.completedAt
      : null;

    const lastKnownPositionResult = {
      source: MapVehiclePositionSource.LAST_KNOWN_POSITION,
      coordinates,
    };
    const lastKnownPointResult = {
      source: MapVehiclePositionSource.LAST_KNOWN_POINT,
      coordinates: lastPointCoordinates,
    };

    if (updatedAt && lastPointCompletedAt && updatedAt > lastPointCompletedAt) {
      return coordinates ? lastKnownPositionResult : unknownLocation;
    }

    return lastPointCoordinates ? lastKnownPointResult : unknownLocation;
  }

  public static handlePointStatusError(
    response: OnyxHttpErrorResponse,
    toastService: OnyxToastService,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    const I18N = 'route';
    const ERRORS: [string, string][] = [
      [
        'Roll back following points before rolling back this point',
        `${I18N}.rollBackFollowingPoints`,
      ],
      [
        'Complete previous points before starting this point',
        `${I18N}.completePreviousPoints`,
      ],
      [
        'A completed fleet swap point cannot be rolled back',
        `${I18N}.rollBackCompletedSwapPoint`,
      ],
    ];

    const error = JSON.stringify(response.error ?? '');

    for (const [match, message] of ERRORS) {
      if (!error.includes(match)) continue;

      toastService.showError({ message });
      response.handled = true;
      break;
    }

    throw response;
  }

  public static getChangeStatusState(
    point: RoutePoint,
    telematicsItem: TelematicsItem | null,
    routeService: RouteService,
  ): { disabled: boolean; tooltip?: string } {
    const I18N = 'route';

    let disabled = !(
      routeService.isCurrent(point) || RouteHelper.isCompleted(point)
    );
    let tooltip = disabled ? `${I18N}.changeStatusDisabled` : undefined;

    if (
      routeService.context === 'order' &&
      telematicsItem &&
      routeService.isFirst(point)
    ) {
      const telematicsRoutePointIndex = telematicsItem.routePoints.findIndex(
        (routePoint) =>
          RouteHelper.getUuid(routePoint) === RouteHelper.getUuid(point),
      );

      if (telematicsRoutePointIndex > 0) {
        const previousPoint =
          telematicsItem.routePoints[telematicsRoutePointIndex - 1];
        const isPreviousFinished = RouteHelper.isFinished(previousPoint);

        if (!isPreviousFinished) {
          disabled = true;
          tooltip = `${I18N}.hasPreviousPoints`;
        }
      }
    }

    return { disabled, tooltip };
  }

  public static getPointIndex(
    point: OrderPoint,
    points: OrderPoint[],
  ): number | null {
    if (!points.length) return null;
    return points.findIndex((p) => p.uuid === point.uuid);
  }

  public static isHandlingPoint(
    point: OrderPointCategory | RoutePoint | RoutePointValue,
  ): boolean {
    if (!isString(point) && RouteHelper.isTechnicalPoint(point)) return false;

    const category = isString(point)
      ? point
      : (RouteHelper.getCategory(point) as OrderPointCategory);
    return [OrderPointCategory.LOADING, OrderPointCategory.UNLOADING].includes(
      category,
    );
  }

  public static getBusinessHours(
    value: OnyxOptional<RoutePointValue>,
  ): BusinessHours | null {
    if (
      value?.category !== OrderPointCategory.CHECKPOINT &&
      value?.category !== OrderPointCategory.PICKUP
    ) {
      return null;
    }

    return value.businessHours as BusinessHours;
  }

  public static areBusinessHoursAlwaysOpen(
    value: OnyxOptional<RoutePointValue>,
  ): boolean {
    const businessHours = RouteHelper.getBusinessHours(value);
    if (!businessHours) return false;

    return Object.values(businessHours).every((openingHours) =>
      isEqual(openingHours, DAY_TIME_RANGE),
    );
  }

  public static isPointNowOpen(
    value: OnyxOptional<RoutePointValue>,
    now: DateTime,
  ): boolean {
    const timeWindows = RoutePointValueHelper.getTimeWindows(value);
    if (timeWindows) {
      const checkInWindow = (
        date: string | null,
        from: string,
        to: string,
      ): boolean => {
        if (!date) return false;

        const start = DateTime.fromISO(`${date}T${from}`);
        const end = DateTime.fromISO(`${date}T${to}`);
        return now >= start && now <= end;
      };

      switch (timeWindows.type) {
        case OrderTimeWindowType.INTERVAL:
        case OrderTimeWindowType.OPENING_HOURS:
          return timeWindows.windows.some(
            ({ date, timeRange }) =>
              timeRange && checkInWindow(date, timeRange.from, timeRange.to),
          );

        case OrderTimeWindowType.FIX: {
          const { date, time } = timeWindows.windows[0];
          return now >= DateTime.fromISO(`${date}T${time}`);
        }
      }
    }

    const transitWindow = RoutePointValueHelper.getTransitTimeWindows(value);
    if (transitWindow) {
      if (transitWindow.type === OrderTransitPointTimeWindowType.ALL_TIME) {
        return true;
      }

      const { date, time } = transitWindow;
      return now >= DateTime.fromISO(`${date}T${time}`);
    }

    const businessHours = RouteHelper.getBusinessHours(value);
    if (businessHours) {
      const openingHours = (() => {
        switch (now.weekday) {
          case 6:
            return businessHours.saturday;
          case 7:
            return businessHours.sunday;
          default:
            return businessHours.weekday;
        }
      })();
      if (!openingHours) return false;

      const from = now.set({
        hour: DateTime.fromISO(openingHours.from).hour,
        minute: DateTime.fromISO(openingHours.from).minute,
      });
      const to = now.set({
        hour: DateTime.fromISO(openingHours.to).hour,
        minute: DateTime.fromISO(openingHours.to).minute,
      });

      return now >= from && now <= to;
    }

    return false;
  }

  public static getServiceTimeDelayColor(point: Point | null, now: DateTime) {
    const isAlwaysOpen =
      RouteHelper.areBusinessHoursAlwaysOpen(point) ||
      RoutePointValueHelper.getTransitTimeWindows(point)?.type ===
        OrderTransitPointTimeWindowType.ALL_TIME;
    if (isAlwaysOpen) return 'black';

    const arrivedAt = point?.timestamps?.arrivedAt;
    const serviceTime = point?.serviceTime;

    if (
      RouteHelper.isPointNowOpen(point, now) &&
      arrivedAt &&
      serviceTime != null
    ) {
      const serviceEnd = DateTime.fromISO(arrivedAt).plus({
        minutes: Duration.fromObject({ seconds: serviceTime }).as('minutes'),
      });
      const delay = DateTime.now().diff(serviceEnd);

      if (delay > SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD) return 'red';
      if (delay > Duration.fromObject({ minutes: 1 })) return 'orange';
    }

    return 'black';
  }

  public static getPointInProgressLabel(
    point: OnyxOptional<OrderPoint | TechnicalPoint>,
    order: Order,
  ): string | null {
    if (!point || order.status.value !== OrderStatus.IN_PROGRESS) {
      return null;
    }

    switch (point.status) {
      case OrderPointStatus.TO_DO:
        return 'labels.driving';

      case OrderPointStatus.WAITING:
        return 'labels.waiting';

      default: {
        switch (point.category) {
          case OrderPointCategory.LOADING:
            return 'labels.loading';

          case OrderPointCategory.UNLOADING:
            return 'labels.unloading';

          default:
            return 'labels.inProgress';
        }
      }
    }
  }

  public static getPointDeadlineTime(
    currentPoint: OnyxOptional<Point>,
  ): DateTime | null {
    if (!currentPoint) return null;

    const deadline = DeadlineHelper.getPointDeadline(currentPoint);
    if (!deadline) return null;

    return currentPoint.status === OrderPointStatus.TO_DO
      ? deadline.arrival
      : deadline.departure;
  }

  public static getPointRouteData(
    currentPoint: TelematicsRoutePoint | Point | null,
    points: Point[],
    now: DateTime,
  ): RoutePointData | null {
    if (!currentPoint) return null;

    const point = RoutePointValueHelper.getValue(currentPoint);
    if (!('status' in point)) return null;

    const compliance = RouteHelper.getScheduleCompliance(point, points);
    const complianceColor = compliance
      ? ScheduleComplianceHelper.getColor(compliance)
      : null;
    const realization = RouteHelper.getRealization(point, now);
    const estimations = RoutePointValueHelper.getEstimations(point);
    const status = RouteHelper.getStatus(point);

    return {
      deadlineTime:
        'deadlineTime' in currentPoint
          ? currentPoint.deadlineTime
          : RouteHelper.getPointDeadlineTime(point),
      bufferTime: 'bufferTime' in currentPoint ? currentPoint.bufferTime : null,
      compliance,
      complianceColor,
      realization,
      estimations: estimations?.arrivalTime ? estimations : null,
      status,
    };
  }

  public static getRouteProposals(routes: OnyxRoute[]): RouteProposal[] {
    return chain(routes)
      .map((route): RouteProposal => ({
        name: '',
        value: route,
        cost: route.summary.totalCost,
        duration: route.summary.duration,
        distance: route.summary.distance,
        types: [],
      }))
      .orderBy([
        (route) => route.cost.resultCurrencyValue,
        (route) => route.duration,
        (route) => route.distance,
      ])
      .thru((options) => {
        const cheapest = minBy(
          options,
          (option) => option.cost.resultCurrencyValue,
        );
        const fastest = minBy(options, (option) => option.duration);
        cheapest?.types.push(OrderFormRouteType.CHEAPEST);
        fastest?.types.push(OrderFormRouteType.FASTEST);

        return options;
      })
      .orderBy(
        [
          (route) => route.types.includes(OrderFormRouteType.CHEAPEST),
          (route) => route.types.includes(OrderFormRouteType.FASTEST),
        ],
        ['desc', 'desc'],
      )
      .map((option) => {
        const types = option.types.length
          ? option.types
          : [OrderFormRouteType.ALTERNATIVE];

        return {
          ...option,
          name: types.length > 1 ? 'optimal' : types[0],
          types,
        };
      })
      .value();
  }

  public static isEmpty(points: RoutePoint[], point: RoutePoint): boolean {
    const index = RouteHelper.getIndex(points, point);
    if (index <= 0) return true;

    const previousPoint = points
      .slice(0, index)
      .findLast((point) => !RouteHelper.isSwapPoint(point));
    if (!previousPoint) return true;

    if (
      RouteHelper.isDelegationPoint(point) ||
      RouteHelper.isDelegationPoint(previousPoint)
    ) {
      return true;
    }

    const hasOrderChanged =
      'order' in point &&
      'order' in previousPoint &&
      !!point.order &&
      !!previousPoint.order &&
      point.order.uuid !== previousPoint.order.uuid;
    if (hasOrderChanged) {
      return point.order.isDedicated || previousPoint.order.isDedicated;
    }

    if (RouteHelper.isHandlingPoint(point)) return false;
    if (RouteHelper.isHandlingPoint(previousPoint)) return false;

    return RouteHelper.isEmpty(points, previousPoint);
  }

  public static isIncludedInOrder(
    point: RoutePoint | RoutePointValue | TechnicalPoint,
  ): boolean {
    const value = RoutePointValueHelper.getValue(point);
    if (RouteHelper.isHandlingPoint(value)) return true;
    if (RouteHelper.isTechnicalPoint(value)) return true;

    return 'includeInOrder' in value ? value.includeInOrder : false;
  }

  public static isProposition(point: RoutePoint): boolean {
    return (
      'order' in point &&
      !!point.order &&
      point.order.status.value === OrderStatus.TO_ACCEPT
    );
  }

  public static getOffRouteData(
    route: OnyxRoutePoint,
    point: OnyxCoordinates,
    arrival?: OnyxCoordinates,
  ): OffRouteData | null {
    const OFF_ROUTE_DISTANCE_THRESHOLD = 1_000;

    interface LegData {
      index: number;
      points: OnyxCoordinates[];
    }

    const legs: LegData[] = chain(route.legs)
      .map((leg, index) => {
        const latLngAltArray = H.geo.LineString.fromFlexiblePolyline(
          leg.polyline,
        ).getLatLngAltArray();

        const points = chunk(latLngAltArray, 3).map(
          ([latitude, longitude, _altitude]) => ({
            latitude,
            longitude,
          }),
        );

        return { index, points };
      })
      .thru((legs) => {
        if (!arrival) return legs;

        legs.at(-1)?.points.push(arrival);
        return legs;
      })
      .value();

    interface LegResult {
      index: number;
      elapsedDistance: number;
      elapsedPoints: number;
      point: OnyxCoordinates;
      offDistance: number;
    }

    const legResult = legs.reduce(
      (closestLegResult: LegResult | null, leg): LegResult => {
        let elapsedDistance = 0;
        let elapsedPoints = 0;

        const legPointPairs = zip(initial(leg.points), tail(leg.points)) as [
          OnyxCoordinates,
          OnyxCoordinates,
        ][];
        const legResults: LegResult[] = [];

        if (leg.points.length === 1) {
          legResults.push({
            index: leg.index,
            elapsedDistance,
            elapsedPoints,
            point: leg.points[0],
            offDistance: AddressHelper.getHaversineDistance(
              point,
              leg.points[0],
            ),
          });
        }

        for (const [start, end] of legPointPairs) {
          const deltaDistance = AddressHelper.getHaversineDistance(start, end);
          elapsedDistance += deltaDistance;
          elapsedPoints += 1;

          const deltaLatitude = end.latitude - start.latitude;
          const deltaLongitude = end.longitude - start.longitude;

          const dotProduct =
            (point.latitude - start.latitude) * deltaLatitude +
            (point.longitude - start.longitude) * deltaLongitude;
          const lengthSquared = deltaLatitude ** 2 + deltaLongitude ** 2;
          const ratio = clamp(dotProduct / lengthSquared, 0, 1);

          const closestPoint = {
            latitude: start.latitude + deltaLatitude * ratio,
            longitude: start.longitude + deltaLongitude * ratio,
          };
          const distance = AddressHelper.getHaversineDistance(
            point,
            closestPoint,
          );

          legResults.push({
            index: leg.index,
            elapsedDistance: elapsedDistance - deltaDistance * (1 - ratio),
            elapsedPoints,
            point: closestPoint,
            offDistance: distance,
          });
        }

        return minBy(
          [closestLegResult ?? legResults[0], ...legResults],
          (result) => result.offDistance,
        )!;
      },
      null,
    );
    if (!legResult) return null;

    const routeProgress = chain(route.legs)
      .slice(0, legResult.index)
      .sumBy((leg) => leg.summary.distance)
      .add(legResult.elapsedDistance)
      .divide(route.summary.distance)
      .value();
    const legProgress = clamp(
      legResult.elapsedDistance / route.legs[legResult.index].summary.distance,
      0,
      1,
    );

    return {
      routeProgress,
      routePoint: legResult.point,
      isCritical: legResult.offDistance > OFF_ROUTE_DISTANCE_THRESHOLD,
      offRouteDistance: legResult.offDistance,
      legIndex: legResult.index,
      legElapsedPoints: legResult.elapsedPoints,
      legProgress,
    };
  }

  public static getRouteLegProgress(
    route: OnyxRoutePoint,
    offRouteData: OffRouteData,
  ): OnyxMapRouteLegProgress {
    const leg = route.legs[offRouteData.legIndex];
    const latLngAltArray = H.geo.LineString.fromFlexiblePolyline(
      leg.polyline,
    ).getLatLngAltArray();
    const latLngArray = chunk(latLngAltArray, 3).flatMap(
      ([latitude, longitude, _altitude]) => [latitude, longitude],
    );

    const { latitude, longitude } = offRouteData.routePoint;
    const completed = H.geo.LineString.fromLatLngArray([
      ...latLngArray.slice(0, offRouteData.legElapsedPoints * 2),
      latitude,
      longitude,
    ]);
    const remaining = H.geo.LineString.fromLatLngArray([
      latitude,
      longitude,
      ...latLngArray.slice(offRouteData.legElapsedPoints * 2),
    ]);

    return {
      index: offRouteData.legIndex,
      completed,
      remaining,
    };
  }

  public static createEmptyRoute(departure: OnyxCoordinates): OnyxRoutePoint {
    return {
      routeHandle: null,
      departure,
      legs: [],
      summary: RouteHelper.createEmptyRouteSummary(),
    };
  }

  public static createEmptyRouteSummary(): OnyxRouteSummary {
    return {
      distance: 0,
      duration: 0,
      countries: [],
      adBlueConsumption: 0,
      adBlueCost: null,
      fuelConsumption: 0,
      fuelCost: null,
      totalFuelCost: null,
      tollsCost: {
        value: 0,
        date: null,
        defaultCurrency: 'eur',
        defaultCurrencyValue: 0,
        currency: 'eur',
        euroValue: 0,
        resultCurrency: 'eur',
        resultCurrencyValue: 0,
      },
      tolls: [],
      totalCost: {
        value: 0,
        date: null,
        defaultCurrency: 'eur',
        defaultCurrencyValue: 0,
        currency: 'eur',
        euroValue: 0,
        resultCurrency: 'eur',
        resultCurrencyValue: 0,
      },
    };
  }

  private static getBufferCompliance(
    timeBuffer: Duration,
    status: OrderPointStatus | null,
  ): OnyxScheduleCompliance {
    if (timeBuffer.toMillis() < 0) return OnyxScheduleCompliance.DELAYED;
    if (
      timeBuffer < SCHEDULE_COMPLIANCE_DELAY_RISK_THRESHOLD &&
      status !== OrderPointStatus.COMPLETED
    ) {
      return OnyxScheduleCompliance.DELAY_RISK;
    }

    return OnyxScheduleCompliance.ON_TIME;
  }

  private static buildRealization(options: {
    status: OrderPointStatus;
    from: DateTime | string;
    to: DateTime | string;
  }): RoutePointRealization {
    const from = isString(options.from)
      ? DateTime.fromISO(options.from)
      : options.from;
    const to = isString(options.to) ? DateTime.fromISO(options.to) : options.to;

    return {
      status: options.status,
      duration: to.diff(from, 'seconds').seconds,
      from,
      to,
      sameDay: from.hasSame(to, 'day'),
    };
  }
}
